import React, { useState, useEffect, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { Transition, Popover } from '@headlessui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Avatar, Badge } from './index';

// Types
export interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
  iconColor?: string; // Optional tailwind text color class for the icon
  children?: NavItem[];
}

export interface NavGroup {
  name: string;
  items: NavItem[];
}

interface SidebarProps {
  navigation: NavGroup[];
  user: {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
  } | null;
  logo: {
    icon: React.ReactNode;
    title: string;
    collapsed?: React.ReactNode;
  };
  onLogout: () => void;
  onOpenCommandPalette?: () => void;
  onOpenSettings?: () => void;
  storageKey?: string;
  quickActions?: Array<{
    name: string;
    icon: React.ComponentType<{ className?: string }>;
    onClick: () => void;
  }>;
}

const Sidebar: React.FC<SidebarProps> = ({
  navigation,
  user,
  logo,
  onLogout,
  onOpenCommandPalette,
  onOpenSettings,
  storageKey = 'sidebarCollapsed',
  quickActions = [],
}) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? JSON.parse(saved) : false;
  });
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(collapsed));
  }, [collapsed, storageKey]);

  // Helper to check if a child path is active
  const isChildPathActive = (childHref: string) => {
    if (location.pathname === childHref) return true;
    const hrefParts = childHref.split('/').filter(Boolean);
    if (hrefParts.length === 1) {
      const pathParts = location.pathname.split('/').filter(Boolean);
      return pathParts.length === 1 && pathParts[0] === hrefParts[0];
    }
    return location.pathname.startsWith(childHref + '/');
  };

  // Auto-expand groups containing active items
  useEffect(() => {
    navigation.forEach(group => {
      group.items.forEach(item => {
        if (item.children) {
          const isActiveChild = item.children.some(child => isChildPathActive(child.href));
          if (isActiveChild) {
            setExpandedItems(prev => ({ ...prev, [item.name]: true }));
          }
        }
      });
    });
  }, [location.pathname, navigation]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const toggleItem = (itemName: string) => {
    setExpandedItems(prev => ({ ...prev, [itemName]: !prev[itemName] }));
  };

  const isItemActive = (href: string) => {
    // Exact match
    if (location.pathname === href) return true;

    // For root paths like /dashboard, /admin, only match exact
    // For nested paths, allow prefix matching
    const hrefParts = href.split('/').filter(Boolean);
    const pathParts = location.pathname.split('/').filter(Boolean);

    // If href is a single segment (like /dashboard), only match exact
    if (hrefParts.length === 1) {
      return pathParts.length === 1 && pathParts[0] === hrefParts[0];
    }

    // For multi-segment hrefs, allow prefix matching
    return location.pathname.startsWith(href + '/');
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <aside
      className={clsx(
        'relative flex flex-col h-screen bg-white border-r border-neutral-200 transition-all duration-300 ease-in-out',
        collapsed ? 'w-[72px]' : 'w-[260px]'
      )}
    >
      {/* Header */}
      <div className={clsx(
        'flex items-center h-16 border-b border-neutral-100 flex-shrink-0 relative',
        collapsed ? 'justify-center px-2' : 'justify-between px-4'
      )}>
        {/* Logo section - clickable to expand when collapsed */}
        <button
          onClick={() => collapsed && setCollapsed(false)}
          className={clsx(
            'flex items-center gap-3 min-w-0',
            collapsed && 'cursor-pointer'
          )}
          title={collapsed ? 'Click to expand sidebar' : undefined}
        >
          <div className={clsx(
            'h-11 w-11 flex-shrink-0 rounded-xl flex items-center justify-center transition-transform duration-200',
            collapsed && 'hover:scale-110'
          )}>
            {logo.icon}
          </div>
          {!collapsed && (
            <span className="text-lg font-bold text-neutral-900 truncate animate-fade-in">
              {logo.title}
            </span>
          )}
        </button>

        {/* Collapse/Expand button */}
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-all duration-200"
            title="Collapse sidebar"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Expand button - shown at bottom when collapsed */}
      {collapsed && (
        <button
          onClick={() => setCollapsed(false)}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 p-2 bg-primary-50 hover:bg-primary-100 text-primary-600 rounded-full shadow-md hover:shadow-lg transition-all duration-200 z-10 group"
          title="Expand sidebar"
        >
          <ChevronRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      )}

      {/* Search trigger */}
      {onOpenCommandPalette && (
        <div className="px-3 py-3 flex-shrink-0">
          <button
            onClick={onOpenCommandPalette}
            className={clsx(
              'w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-500 bg-neutral-50 hover:bg-neutral-100 rounded-lg border border-neutral-200 transition-all duration-200',
              collapsed && 'justify-center px-2'
            )}
          >
            <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">Search...</span>
                <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 bg-white rounded border border-neutral-200">
                  ⌘K
                </kbd>
              </>
            )}
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 space-y-4 scrollbar-thin">
        {navigation.map((group) => (
          <div key={group.name}>
            {/* Group header */}
            {!collapsed && group.name && (
              <button
                onClick={() => toggleGroup(group.name)}
                className="w-full flex items-center justify-between px-3 py-1 text-[11px] font-semibold text-neutral-400 uppercase tracking-wider hover:text-neutral-600 transition-colors"
              >
                {group.name}
                <ChevronDownIcon
                  className={clsx(
                    'h-3 w-3 transition-transform duration-200',
                    expandedGroups[group.name] === false ? '-rotate-90' : ''
                  )}
                />
              </button>
            )}

            {/* Group items */}
            <div
              className={clsx(
                'mt-1 space-y-0.5 transition-all duration-200',
                expandedGroups[group.name] === false && !collapsed ? 'hidden' : ''
              )}
            >
              {group.items.map((item) => {
                const isActive = isItemActive(item.href);
                const children = item.children;
                const hasChildren = children && children.length > 0;
                const isExpanded = expandedItems[item.name];
                const Icon = item.icon;
                const hasActiveChild = hasChildren && children.some(c => isItemActive(c.href));

                return (
                  <div key={item.name} className="relative">
                    {hasChildren ? (
                      <>
                        <button
                          onClick={() => toggleItem(item.name)}
                          onMouseEnter={() => collapsed && setHoveredItem(item.name)}
                          onMouseLeave={() => setHoveredItem(null)}
                          className={clsx(
                            'w-full group flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200',
                            isActive || hasActiveChild
                              ? 'text-primary-700 bg-primary-50'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900',
                            collapsed && 'justify-center px-2'
                          )}
                        >
                          <Icon
                            className={clsx(
                              'h-5 w-5 flex-shrink-0 transition-colors duration-200',
                              isActive || hasActiveChild
                                ? 'text-primary-600'
                                : item.iconColor || 'text-neutral-400 group-hover:text-neutral-600'
                            )}
                          />
                          {!collapsed && (
                            <>
                              <span className="flex-1 text-left truncate">{item.name}</span>
                              <ChevronDownIcon
                                className={clsx(
                                  'h-4 w-4 text-neutral-400 transition-transform duration-200',
                                  isExpanded ? 'rotate-180' : ''
                                )}
                              />
                            </>
                          )}
                        </button>

                        {/* Collapsed tooltip with children */}
                        {collapsed && hoveredItem === item.name && (
                          <div className="absolute left-full top-0 ml-2 z-50 w-48 bg-white rounded-xl shadow-xl border border-neutral-200 py-2 animate-fade-in">
                            <div className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider border-b border-neutral-100">
                              {item.name}
                            </div>
                            {children.map((child) => {
                              const ChildIcon = child.icon;
                              return (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  className={clsx(
                                    'flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                                    isItemActive(child.href)
                                      ? 'text-primary-700 bg-primary-50'
                                      : 'text-neutral-600 hover:bg-neutral-50'
                                  )}
                                >
                                  <ChildIcon className="h-4 w-4" />
                                  {child.name}
                                </Link>
                              );
                            })}
                          </div>
                        )}

                        {/* Expanded children */}
                        {!collapsed && (
                          <div
                            className={clsx(
                              'overflow-hidden transition-all duration-200',
                              isExpanded ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'
                            )}
                          >
                            <div className="ml-4 pl-4 border-l-2 border-neutral-100 space-y-0.5">
                              {children.map((child) => {
                                const ChildIcon = child.icon;
                                const isChildActive = isItemActive(child.href);
                                return (
                                  <Link
                                    key={child.name}
                                    to={child.href}
                                    className={clsx(
                                      'group flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all duration-200',
                                      isChildActive
                                        ? 'text-primary-700 bg-primary-50 font-medium'
                                        : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50'
                                    )}
                                  >
                                    <ChildIcon
                                      className={clsx(
                                        'h-4 w-4 flex-shrink-0 transition-colors',
                                        isChildActive
                                          ? 'text-primary-600'
                                          : 'text-neutral-400 group-hover:text-neutral-500'
                                      )}
                                    />
                                    <span className="truncate">{child.name}</span>
                                    {child.badge && (
                                      <Badge variant="primary" size="sm" className="ml-auto">
                                        {child.badge}
                                      </Badge>
                                    )}
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <Link
                          to={item.href}
                          onMouseEnter={() => collapsed && setHoveredItem(item.name)}
                          onMouseLeave={() => setHoveredItem(null)}
                          className={clsx(
                            'group flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 relative',
                            isActive
                              ? 'text-white bg-gradient-to-r from-primary-600 to-primary-500 shadow-md shadow-primary-500/25'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900',
                            collapsed && 'justify-center px-2'
                          )}
                        >
                          <Icon
                            className={clsx(
                              'h-5 w-5 flex-shrink-0 transition-colors duration-200',
                              isActive
                                ? 'text-white'
                                : item.iconColor || 'text-neutral-400 group-hover:text-neutral-600'
                            )}
                          />
                          {!collapsed && (
                            <>
                              <span className="truncate">{item.name}</span>
                              {item.badge && (
                                <Badge
                                  variant={isActive ? 'neutral' : 'primary'}
                                  size="sm"
                                  className="ml-auto"
                                >
                                  {item.badge}
                                </Badge>
                              )}
                            </>
                          )}
                        </Link>

                        {/* Collapsed tooltip */}
                        {collapsed && hoveredItem === item.name && (
                          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 px-3 py-1.5 bg-neutral-900 text-white text-sm rounded-lg whitespace-nowrap animate-fade-in">
                            {item.name}
                            {item.badge && (
                              <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                                {item.badge}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <div className={clsx('px-3 py-2 flex-shrink-0', collapsed ? 'flex justify-center' : '')}>
          <Popover className="relative">
            <Popover.Button
              className={clsx(
                'flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-200',
                collapsed ? 'justify-center w-10 px-0' : 'w-full'
              )}
            >
              <PlusIcon className="h-5 w-5" />
              {!collapsed && <span>Quick Action</span>}
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                className={clsx(
                  'absolute z-50 w-56 bg-white rounded-xl shadow-xl border border-neutral-200 py-2',
                  collapsed ? 'left-full bottom-0 ml-2' : 'bottom-full left-0 mb-2'
                )}
              >
                {quickActions.map((action) => {
                  const ActionIcon = action.icon;
                  return (
                    <button
                      key={action.name}
                      onClick={action.onClick}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      <ActionIcon className="h-5 w-5 text-neutral-400" />
                      {action.name}
                    </button>
                  );
                })}
              </Popover.Panel>
            </Transition>
          </Popover>
        </div>
      )}

      {/* User section */}
      <div className="flex-shrink-0 border-t border-neutral-100 p-3 bg-gradient-to-r from-neutral-50 to-white">
        <div
          className={clsx(
            'flex items-center gap-3 p-2 rounded-xl hover:bg-neutral-100 transition-colors cursor-pointer',
            collapsed && 'justify-center'
          )}
        >
          <div className="relative">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-semibold text-sm shadow-md">
              {getInitials()}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-success-500 rounded-full border-2 border-white"></div>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-neutral-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-neutral-500 truncate">{user?.role}</p>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className={clsx('mt-2 flex gap-1', collapsed && 'flex-col')}>
          {onOpenSettings && (
            <button
              onClick={onOpenSettings}
              className={clsx(
                'flex items-center justify-center gap-2 p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors',
                !collapsed && 'flex-1'
              )}
              title="Settings"
            >
              <Cog6ToothIcon className="h-5 w-5" />
              {!collapsed && <span className="text-sm">Settings</span>}
            </button>
          )}
          <button
            onClick={onLogout}
            className={clsx(
              'flex items-center justify-center gap-2 p-2 text-neutral-500 hover:text-error-600 hover:bg-error-50 rounded-lg transition-colors',
              !collapsed && 'flex-1'
            )}
            title="Logout"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            {!collapsed && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
